import {
  getV1CompaniesCompanyIdProjects,
  GetV1CompaniesCompanyIdProjectsData,
  getV1CompaniesCompanyIdProjectsExternalIdsTypeahead,
  GetV1CompaniesCompanyIdProjectsExternalIdsTypeaheadData,
  getV1CompaniesCompanyIdProjectsId,
  getV1CompaniesCompanyIdProjectsProjectIdPhasesTypeahead,
  GetV1CompaniesCompanyIdProjectsProjectIdPhasesTypeaheadData,
  getV1CompaniesCompanyIdProjectsTypeahead,
  GetV1CompaniesCompanyIdProjectsTypeaheadData,
  patchV1CompaniesCompanyIdProjectsId,
  postV1CompaniesCompanyIdProjects,
  type Project_Create,
  type Project_Update,
  ProjectCreateState,
  ProjectState,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';

import { ProjectStatusFilter } from '~constants/enums';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import {
  PhaseBasic,
  Project,
  ProjectExternalIdTypeahead,
  ProjectTypeahead,
} from '~hooks/useProjects/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink, PaginationQuery } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface CreateProjectV2Params {
  initialState?: 'draft' | 'active';
  body: Project_Create;
}

interface UpdateProjectV2Params {
  body: Partial<Project_Update>;
  id: string;
}

interface GetProjectsByCompanyIdProps {
  companyId: string;
  link?: PaginationLink;
  searchQuery?: string;
  filterParams?: { states: (ProjectStatusFilter | null)[] };
}

interface GetProjectsByCompanyIdTypeaheadProps {
  accountId?: string;
  companyId: string;
  cursor?: string;
  limit?: number;
  query?: string;
  states?: ProjectState[];
}

interface GetProjectExternalIdsTypeaheadProps {
  companyId: string;
  cursor?: string;
  limit?: number;
  query?: string;
}

interface GetProjectPhasesTypeaheadProps {
  companyId: string;
  cursor?: string;
  limit?: number;
  projectId: string;
  query?: string;
}

type GetProjectsByCompanyIdQuery = GetV1CompaniesCompanyIdProjectsData['query'] &
  PaginationQuery;

// Maps the FE-facing 'draft' | 'active' literal to the ProjectCreateState enum
// member Project_Create.state expects.
const PROJECT_CREATE_STATE_BY_INITIAL_STATE: Record<
  'draft' | 'active',
  ProjectCreateState
> = {
  draft: ProjectCreateState.DRAFT,
  active: ProjectCreateState.ACTIVE,
};

export default function useProjects() {
  const { projectsStore, userStore } = useStores();
  const { addSearchHeaderParam } = useDataGridSearch();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);

  const getProjectById = async (id: string) => {
    try {
      const response = await getV1CompaniesCompanyIdProjectsId({
        path: { 'company-id': effectiveCompanyId, id },
      });

      return Project.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.projects.failed_to_fetch_project'),
      );
      throw error;
    }
  };

  const getProjectPhasesTypeahead = async (options: GetProjectPhasesTypeaheadProps) => {
    const query: GetV1CompaniesCompanyIdProjectsProjectIdPhasesTypeaheadData['query'] = {
      'page[after]': options.cursor || undefined,
      'page[limit]': options.limit ?? 30,
      'search[query]': options.query || undefined,
    };

    try {
      const response = await getV1CompaniesCompanyIdProjectsProjectIdPhasesTypeahead({
        path: { 'company-id': options.companyId, 'project-id': options.projectId },
        query,
      });

      const phases = response.data.data.map((phase) => {
        return PhaseBasic.parse(phase);
      });

      return { data: phases, pagination: extractPagination(response) };
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.projects.failed_to_fetch_phases'),
        );
      } catch {
        // Error already surfaced via toaster + Sentry in handleRequestError.
      }

      return undefined;
    }
  };

  const createProjectV2 = async ({ initialState, body }: CreateProjectV2Params) => {
    try {
      const initialProjectState = initialState
        ? PROJECT_CREATE_STATE_BY_INITIAL_STATE[initialState]
        : undefined;
      const createCompanyId = body.company_id ?? effectiveCompanyId;
      const response = await postV1CompaniesCompanyIdProjects({
        body: {
          ...body,
          company_id: createCompanyId,
          ...(initialProjectState ? { state: initialProjectState } : {}),
        },
        path: { 'company-id': createCompanyId },
      });
      const projectSaved = Project.parse(response.data.data);

      projectsStore.addAProject(projectSaved);

      return projectSaved;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.projects.failed_to_create'));
      throw error;
    }
  };

  const updateProjectV2 = async ({ body, id }: UpdateProjectV2Params) => {
    try {
      const response = await patchV1CompaniesCompanyIdProjectsId({
        body,
        path: { 'company-id': effectiveCompanyId, id },
      });
      const projectSaved = Project.parse(response.data.data);

      projectsStore.updateProject(projectSaved);

      return projectSaved;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.projects.failed_to_update'));
      throw error;
    }
  };

  const getProjectsByCompanyIdPaginated = async ({
    companyId,
    filterParams,
    link,
    searchQuery,
  }: GetProjectsByCompanyIdProps) => {
    let query: GetProjectsByCompanyIdQuery = {
      'page[limit]': projectsStore.projectsPagination.limit,
    };

    const normalizedFilterParams = filterParams
      ? {
          ...filterParams,
          states: filterParams.states.filter(
            (state): state is ProjectStatusFilter => state !== null,
          ),
        }
      : undefined;

    if (link && projectsStore.projectsPagination[link]) {
      query[`page[${link}]`] = projectsStore.projectsPagination[link];
    }

    query = addSearchHeaderParam({
      filterParams: normalizedFilterParams,
      params: query,
      searchValue: searchQuery,
    }) as GetProjectsByCompanyIdQuery;

    try {
      const response = await getV1CompaniesCompanyIdProjects({
        path: { 'company-id': companyId },
        query,
      });

      const projects = response.data.data.map(Project.parse);
      const pagination = extractPagination(response);

      projectsStore.setProjects(projects);
      projectsStore.setProjectsPagination(pagination);
      projectsStore.updateProjectsPageNumber(link);

      return projects;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.projects.failed_to_fetch'));
      throw error;
    }
  };

  const getProjectsByCompanyIdTypeahead = async (
    options: GetProjectsByCompanyIdTypeaheadProps,
  ) => {
    const query: GetV1CompaniesCompanyIdProjectsTypeaheadData['query'] = {
      'filter[account_id]': options.accountId || undefined,
      'filter[states]': options.states?.length ? options.states : undefined,
      'page[after]': options.cursor || undefined,
      'page[limit]': options.limit ?? 30,
      'search[query]': options.query || undefined,
    };

    try {
      const response = await getV1CompaniesCompanyIdProjectsTypeahead({
        path: { 'company-id': options.companyId },
        query,
      });

      const projects = response.data.data.map((project) => {
        return ProjectTypeahead.parse(project);
      });

      return { data: projects, pagination: extractPagination(response) };
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.projects.failed_to_fetch'),
        );
      } catch {
        // Error already surfaced via toaster + Sentry in handleRequestError.
      }

      return undefined;
    }
  };

  const getProjectExternalIdsTypeahead = async (
    options: GetProjectExternalIdsTypeaheadProps,
  ) => {
    const query: GetV1CompaniesCompanyIdProjectsExternalIdsTypeaheadData['query'] = {
      'page[after]': options.cursor || undefined,
      'page[limit]': options.limit ?? 30,
      'search[query]': options.query || undefined,
    };

    try {
      const response = await getV1CompaniesCompanyIdProjectsExternalIdsTypeahead({
        path: { 'company-id': options.companyId },
        query,
      });

      const projectsExternalIds = response.data.data.map((projectExternalId) => {
        return ProjectExternalIdTypeahead.parse(projectExternalId);
      });

      return { data: projectsExternalIds, pagination: extractPagination(response) };
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.projects.failed_to_fetch_external_ids'),
        );
      } catch {
        // Error already surfaced via toaster + Sentry in handleRequestError.
      }

      return undefined;
    }
  };

  return {
    createProjectV2,
    getProjectById,
    getProjectExternalIdsTypeahead,
    getProjectPhasesTypeahead,
    getProjectsByCompanyIdPaginated,
    getProjectsByCompanyIdTypeahead,
    updateProjectV2,
  } as const;
}
